import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Download, Smartphone, Apple, Chrome, Copy, Check, MonitorDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatErr } from "@/lib/api";

const WINDOWS_EXE_URL = "https://github.com/muazmusellemworkmail-png/Muhasebety_full/releases/latest/download/MuhasabatySetup.exe";

export default function InstallPWA() {
  const { user, currentCompany } = useAuth();
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activationBusy, setActivationBusy] = useState(false);

  const appUrl = typeof window !== "undefined" ? window.location.origin : "";
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=8&color=1E3A8A&data=${encodeURIComponent(appUrl)}`;

  useEffect(() => {
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setInstalled(isStandalone);
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", () => { setInstalled(true); setDeferred(null); });
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  if (installed) return null;

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
    } else {
      setShowGuide(true);
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(appUrl); setCopied(true); toast.success("تم نسخ الرابط"); setTimeout(()=>setCopied(false), 2000); }
    catch { toast.error("لم يتم النسخ"); }
  };

  const canDownloadWindows = Boolean(
    user && currentCompany?.sold &&
    ["super_admin", "company_manager"].includes(user.role)
  );

  const activateWindows = async () => {
    setActivationBusy(true);
    try {
      const { data } = await api.post("/offline/activation-session");
      const localUrl = `http://127.0.0.1:8001/login?activation=${encodeURIComponent(data.activation_token)}&cloud_url=${encodeURIComponent(appUrl)}`;
      window.open(localUrl, "_blank", "noopener");
      toast.success("تم تجهيز التفعيل — سيتم ربط النسخة المحلية تلقائياً دون إدخال كود");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setActivationBusy(false); }
  };

  return (
    <>
      {canDownloadWindows && <div className="hidden lg:flex items-center gap-1">
        <a href={WINDOWS_EXE_URL} download data-testid="download-windows-local-button" onClick={() => toast.info("سيبدأ تنزيل مثبت Windows الدائم الخاص بالشركة المباعة")} title="تنزيل نسخة Windows الدائمة للشركة المباعة">
          <Button variant="outline" size="sm" className="gap-2 h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-50"><MonitorDown className="w-4 h-4" /><span className="hidden xl:inline">نسخة Windows</span></Button>
        </a>
        <Button variant="outline" size="sm" onClick={activateWindows} disabled={activationBusy} className="gap-2 h-9 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="activate-windows-local-button" title="تفعيل النسخة المحلية دون كود"><MonitorDown className="w-4 h-4" /><span className="hidden xl:inline">تفعيل محلي تلقائي</span></Button>
      </div>}
      <Button variant="outline" size="sm" onClick={install} className="gap-2 h-9" data-testid="install-pwa-button">
        <Download className="w-4 h-4" />
        <span className="hidden sm:inline">تثبيت التطبيق</span>
      </Button>
      <Dialog open={showGuide} onOpenChange={setShowGuide}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="install-guide-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl"><Smartphone className="w-5 h-5" /> ثبّت «محاسبتي» على هاتفك</DialogTitle>
            <DialogDescription>تطبيق ويب قابل للتثبيت (PWA) — يعمل بدون متجر، ويعطي صلاحية كاملة للكاميرا.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-6 mt-2">
            <div className="space-y-3">
              <div className="border-2 border-primary/20 rounded-md p-4 bg-primary/5 text-center">
                <div className="text-xs font-bold text-primary uppercase tracking-wider mb-2">امسح الباركود بكاميرا هاتفك</div>
                <img src={qrUrl} alt="QR Code" className="mx-auto rounded bg-white p-2" width="240" height="240" data-testid="install-qr" />
                <div className="text-xs text-muted-foreground mt-2">افتح كاميرا الهاتف → وجّهها للباركود → اضغط الرابط</div>
              </div>
              <div className="flex items-center gap-2 border border-border rounded-md p-2 bg-muted/40">
                <code className="flex-1 text-xs font-mono truncate text-muted-foreground" dir="ltr" data-testid="install-app-url">{appUrl}</code>
                <Button size="sm" variant="ghost" onClick={copyLink} data-testid="copy-link-button">
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-3 text-sm leading-relaxed">
              <div className="border border-border rounded-md p-4 bg-muted/40">
                <div className="flex items-center gap-2 font-bold mb-2 text-emerald-700"><Chrome className="w-4 h-4" /> Android (Chrome / Edge)</div>
                <ol className="list-decimal ps-5 space-y-1 text-muted-foreground">
                  <li>افتح الرابط بعد مسح الباركود</li>
                  <li>افتح قائمة المتصفح (النقاط الثلاث)</li>
                  <li>اختر <b>«تثبيت التطبيق»</b> أو <b>«إضافة إلى الشاشة الرئيسية»</b></li>
                  <li>اضغط «تثبيت» — سيظهر أيقونة على الشاشة</li>
                </ol>
              </div>
              <div className="border border-border rounded-md p-4 bg-muted/40">
                <div className="flex items-center gap-2 font-bold mb-2 text-slate-700"><Apple className="w-4 h-4" /> iPhone (Safari)</div>
                <ol className="list-decimal ps-5 space-y-1 text-muted-foreground">
                  <li>افتح الرابط في <b>Safari</b> (وليس Chrome)</li>
                  <li>اضغط زر المشاركة <span className="inline-block px-2 py-0.5 border rounded text-xs">⬆︎</span> في الأسفل</li>
                  <li>اختر <b>«إضافة إلى الشاشة الرئيسية»</b></li>
                  <li>اضغط «إضافة» في الأعلى</li>
                </ol>
              </div>
              <div className="border border-amber-200 bg-amber-50 rounded-md p-3 text-xs text-amber-900">
                <b>ملاحظة:</b> هذا ليس APK ولا موجود في متجر Google Play — هو <b>تطبيق ويب متقدم (PWA)</b> يتثبّت مباشرة من المتصفح. بعد التثبيت رح يشتغل زي أي تطبيق أصلي مع صلاحيات كاميرا كاملة وبدون شريط عنوان.
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={()=>setShowGuide(false)}>حسناً</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
